/**
 * Chart primitives — inline SVG, no dependency.
 *
 * ## Why every chart here is single-hue
 *
 * The design system's chart tokens (`--chart-1..5`) are deliberately warm and
 * desaturated, which is right for the product and wrong for encoding identity:
 * run through a CVD validator, the adjacent pairs fail badly. Chocolate against
 * rust is ΔE 5.3 for *normal* vision in dark mode — well under the 15 floor —
 * so two categories filled with two of these hues are not reliably tellable
 * apart by anyone, colour-blind or not.
 *
 * Rather than introduce a second, louder palette that would clash with the rest
 * of the app, every chart in this file encodes with **length and position, and
 * never with hue**:
 *
 *  - magnitude comparisons are single-hue bars (colour carries no information,
 *    so there is nothing to confuse);
 *  - two related series become **small multiples** — two stacked panels sharing
 *    an x domain — rather than a stacked or dual-colour chart;
 *  - state (healthy / failing) is carried by a labelled status dot next to the
 *    text, never by the fill, which is the rule for status colour anyway.
 *
 * The upshot is that these charts are legible in both themes, under any colour
 * vision, and in greyscale print, and none of that depended on taste.
 *
 * Mark specs follow one fixed set: bars ≤24px with a 4px rounded data-end and a
 * square baseline, a 2px surface gap between neighbours, 2px lines, hairline
 * recessive axes, and labels only on the extremes — never a number on every bar.
 */

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { fmtNumber } from '@/shared/lib/format'

/** The one data hue. Everything else on a chart is ink or surface. */
const HUE = 'var(--chart-1)'

export interface Point {
  label: string
  value: number
  /** Optional secondary value shown only in the tooltip. */
  hint?: string
}

// ---------------------------------------------------------------------------

/** Positioned by percentage so it tracks the column at any panel width. */
function Tooltip({ leftPct, children }: { leftPct: number; children: React.ReactNode }) {
  return (
    <div
      className="pointer-events-none absolute -top-1 z-20 -translate-x-1/2 -translate-y-full rounded-md border border-border bg-popover px-2 py-1 text-erp-xs text-popover-foreground shadow-md whitespace-nowrap"
      style={{ left: `${Math.min(Math.max(leftPct, 8), 92)}%` }}
    >
      {children}
    </div>
  )
}

/**
 * A single-series column chart sized for a panel, not a dashboard hero.
 *
 * Laid out with flex boxes rather than SVG, and that is a correction rather than
 * a preference. The first version drew `<rect>`s into a 100-unit viewBox with
 * `preserveAspectRatio="none"`, which stretches the x axis by whatever the panel
 * happens to be wide: bars rendered ~80px thick against a 24px cap, and the 4px
 * rounded data-end came out as a wide ellipse. Percentage-width divs give exact
 * pixel control of the cap, the gap and the corner radius at any width, with no
 * scaling maths to get wrong.
 *
 * Used in pairs (requests above, failures below) as small multiples. Each panel
 * is one series, so there is no legend — the panel's own title says what it is,
 * and a legend box with one swatch would just restate it.
 *
 * Each panel scales to its own peak, and the peak is labelled. Sharing a scale
 * across the pair would be more honest about proportion but would flatten the
 * failures panel to invisibility when failures are 4% of volume, which defeats
 * the reason for showing it at all. The label is what stops the two being
 * misread as comparable heights.
 */
export function Sparkbars({
  points,
  height = 84,
  emptyMessage = 'No activity in this window.',
  formatValue = fmtNumber,
  className,
}: {
  points: Point[]
  height?: number
  emptyMessage?: string
  formatValue?: (n: number) => string
  className?: string
}) {
  const [hover, setHover] = useState<number | null>(null)

  if (points.length === 0) {
    return (
      <div
        className={cn('flex items-center justify-center text-erp-xs text-muted-foreground', className)}
        style={{ height }}
      >
        {emptyMessage}
      </div>
    )
  }

  const peakValue = Math.max(...points.map((p) => p.value), 0)
  // The divisor is clamped so an all-zero series does not divide by zero; the
  // *label* uses the real peak, because labelling "1" over an empty chart names
  // a number that appears nowhere in the data.
  const max = Math.max(peakValue, 1)
  const peak = points.reduce((best, p, i) => (p.value > (points[best]?.value ?? -1) ? i : best), 0)
  const hovered = hover != null ? points[hover] : undefined

  // Room for the peak label above the tallest column, so it never sits on top
  // of the data or gets clipped by the panel.
  const LABEL_ROW = 14
  const plot = Math.max(height - LABEL_ROW, 16)

  return (
    <div className={cn('relative', className)}>
      <div className="text-erp-xs tabular-nums text-muted-foreground" style={{ height: LABEL_ROW }}>
        {peakValue > 0 && (
          <span
            className="absolute -translate-x-1/2 whitespace-nowrap"
            // Clamped like the tooltip: at the first or last bucket an
            // unclamped centre puts half the number outside the panel.
            style={{ left: `${Math.min(Math.max(((peak + 0.5) / points.length) * 100, 8), 92)}%` }}
          >
            {formatValue(peakValue)}
          </span>
        )}
      </div>

      <div
        className="flex items-end gap-[2px] border-b border-border"
        style={{ height: plot }}
        role="img"
        // `peakValue`, not `max`. `max` is floored at 1 so an all-zero series
        // still has a divisor; announcing it would tell a screen-reader user the
        // peak is 1 on a chart whose every value is 0.
        aria-label={
          peakValue > 0
            ? `Column chart, ${points.length} buckets, peak ${formatValue(peakValue)}`
            : `Column chart, ${points.length} buckets, nothing recorded`
        }
        onMouseLeave={() => setHover(null)}
      >
        {points.map((p, i) => (
          <div
            key={i}
            className="flex-1 flex items-end justify-center h-full min-w-0 focus:outline-none focus-visible:ring-1 focus-visible:ring-chocolate rounded-sm"
            tabIndex={0}
            role="img"
            aria-label={`${p.label}: ${formatValue(p.value)}`}
            onMouseEnter={() => setHover(i)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(null)}
          >
            <div
              // ≤24px: never fill the slot, let the leftover be air.
              className="w-full max-w-[24px] rounded-t-[4px] transition-opacity"
              style={{
                height: `${Math.max(p.value === 0 ? 0 : 2, (p.value / max) * 100)}%`,
                background: HUE,
                opacity: hover != null && hover !== i ? 0.5 : 1,
              }}
              title={`${p.label}: ${formatValue(p.value)}`}
            />
          </div>
        ))}
      </div>

      {hovered && hover != null && (
        <Tooltip leftPct={((hover + 0.5) / points.length) * 100}>
          <span className="text-muted-foreground">{hovered.label}</span>{' '}
          <span className="font-medium tabular-nums">{formatValue(hovered.value)}</span>
          {hovered.hint && <span className="text-muted-foreground"> · {hovered.hint}</span>}
        </Tooltip>
      )}
    </div>
  )
}

/**
 * Horizontal ranked bars — the right form when the categories have long names
 * and the reader's job is "which is biggest".
 *
 * Single hue: length already encodes magnitude, so colour would be decoration.
 * `tone` puts a status dot beside the *label*, which is how state is allowed to
 * be coloured — with a shape and a word next to it, never a fill on its own.
 */
export function RankedBars({
  points,
  max: maxOverride,
  tone,
  emptyMessage = 'Nothing recorded yet.',
  formatValue = fmtNumber,
  className,
}: {
  points: Point[]
  max?: number
  tone?: (label: string) => 'good' | 'warn' | 'bad' | undefined
  emptyMessage?: string
  formatValue?: (n: number) => string
  className?: string
}) {
  if (points.length === 0) {
    return <div className={cn('py-6 text-center text-erp-xs text-muted-foreground', className)}>{emptyMessage}</div>
  }

  const max = maxOverride ?? Math.max(...points.map((p) => p.value), 1)

  return (
    <ul className={cn('space-y-1.5', className)}>
      {points.map((p) => {
        const t = tone?.(p.label)
        return (
          <li key={p.label} className="grid grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-2">
            <span className="flex items-center gap-1.5 min-w-0">
              {t && (
                <span
                  aria-hidden
                  className={cn(
                    'h-1.5 w-1.5 rounded-full shrink-0',
                    t === 'good' && 'bg-emerald-600',
                    t === 'warn' && 'bg-amber-500',
                    t === 'bad' && 'bg-red-600',
                  )}
                />
              )}
              <span className="truncate text-erp-xs text-muted-foreground" title={p.label}>
                {p.label}
              </span>
            </span>
            <span className="h-2.5 bg-muted/60 rounded-sm overflow-hidden">
              <span
                className="block h-full rounded-r-sm"
                style={{
                  width: `${Math.max(p.value === 0 ? 0 : 2, (p.value / max) * 100)}%`,
                  background: HUE,
                }}
              />
            </span>
            {/* Value at the tip — the axis a horizontal bar chart does not have. */}
            <span className="text-erp-xs tabular-nums text-foreground w-14 text-right">
              {formatValue(p.value)}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * A labelled panel around a chart. Keeps the title/description typography
 * identical everywhere so a row of charts reads as one system.
 */
export function ChartFrame({
  title,
  description,
  right,
  children,
  className,
}: {
  title: string
  description?: string
  right?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <div className="min-w-0">
          <div className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground truncate">
            {title}
          </div>
          {description && <div className="text-erp-xs text-muted-foreground/80 truncate">{description}</div>}
        </div>
        {right}
      </div>
      {children}
    </div>
  )
}

/** Axis labels for a bucket series — first, middle and last only. */
export function BucketAxis({ points }: { points: Point[] }) {
  if (points.length === 0) return null
  const at = [0, Math.floor(points.length / 2), points.length - 1]
  const seen = new Set<number>()
  return (
    <div className="flex justify-between text-erp-xs text-muted-foreground/70 mt-1">
      {at.map((i) => {
        const p = points[i]
        if (!p || seen.has(i)) return null
        seen.add(i)
        return <span key={i}>{p.label}</span>
      })}
    </div>
  )
}

/**
 * A funnel: how many survive each stage of one flow.
 *
 * **Ordinal, not categorical.** Swapping two stages would change the meaning, so the
 * colour is a one-hue ramp with monotone lightness (`--ordinal-1..5`) and the reader
 * sees the order in the colour without having to tell two hues apart. Colouring these
 * bars by category would spend the identity channel on something position already says.
 *
 * The **drop** between stages is the information, so it is printed between the bars
 * rather than left to be inferred from two lengths. A funnel where every stage is
 * labelled with its own count and nothing else is a bar chart with extra steps.
 *
 * Bars are proportional to the FIRST stage, never each to its own maximum: a funnel
 * whose stages each fill the width says nothing at all.
 */
export function FunnelBars({
  stages, className, formatValue = fmtNumber,
  dropLabel = (n, pct) => `−${n} (${pct}%) did not continue`,
  holdLabel = 'all continued',
}: {
  stages: { label: string; value: number; hint?: string }[]
  className?: string
  formatValue?: (n: number) => string
  /**
   * What the gap between two stages MEANS. The default reads it as attrition, which is
   * right for a contact or recruitment funnel — somebody was lost. It is wrong for a
   * worklist, where a stage holds items that have not been worked yet rather than items
   * that failed, and "did not continue" then accuses the team of losing work it is in the
   * middle of doing.
   */
  dropLabel?: (formatted: string, pct: string) => string
  holdLabel?: string
}) {
  if (stages.length === 0) {
    return <div className={cn('py-6 text-center text-erp-xs text-muted-foreground', className)}>Nothing in this range.</div>
  }
  const top = Math.max(stages[0]!.value, 1)

  return (
    <ol className={cn('space-y-0', className)}>
      {stages.map((s, i) => {
        const pctOfTop = (s.value / top) * 100
        const prev = i > 0 ? stages[i - 1]!.value : null
        const dropped = prev !== null ? prev - s.value : 0
        const dropPct = prev && prev > 0 ? (dropped / prev) * 100 : 0
        return (
          <li key={s.label}>
            {i > 0 && (
              <div className="flex items-center gap-2 py-1 pl-1 text-erp-xs text-muted-foreground">
                <span className="inline-block w-px h-4 bg-border ml-1.5" />
                {dropped > 0
                  ? <span>{dropLabel(formatValue(dropped), dropPct.toFixed(0))}</span>
                  : <span>{holdLabel}</span>}
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="text-erp-sm truncate">{s.label}</span>
                  <span className="text-erp-sm font-medium tabular-nums shrink-0">
                    {formatValue(s.value)}
                    <span className="text-erp-xs text-muted-foreground ml-1.5">
                      {((s.value / top) * 100).toFixed(0)}%
                    </span>
                  </span>
                </div>
                <div className="h-3 w-full rounded-[3px] bg-muted/60 overflow-hidden">
                  <div
                    className="h-full rounded-[3px]"
                    style={{
                      width: `${Math.max(pctOfTop, s.value > 0 ? 1.5 : 0)}%`,
                      background: `var(--ordinal-${Math.min(i + 1, 5)})`,
                    }}
                    title={s.hint ?? `${s.label}: ${formatValue(s.value)}`}
                  />
                </div>
              </div>
            </div>
          </li>
        )
      })}
    </ol>
  )
}

/**
 * A part-to-whole strip for OUTCOMES — the one place a category may be coloured.
 *
 * Status is a reserved scale with fixed meaning (good / warning / serious / critical),
 * and the rule that comes with it is that it never travels alone: every segment carries
 * a label and a count beneath the strip, so the colour is a reinforcement rather than
 * the only way to read it. That is also what makes it safe under any colour vision.
 *
 * Segments are separated by 2px of surface, not by a border, so the boundary is a gap
 * rather than another colour to perceive.
 */
const OUTCOME_TONE: Record<string, string> = {
  good: 'bg-emerald-600',
  warn: 'bg-amber-500',
  bad: 'bg-red-600',
  neutral: 'bg-muted-foreground/40',
}

export function OutcomeStrip({
  parts, className, formatValue = fmtNumber,
}: {
  parts: { label: string; value: number; tone: 'good' | 'warn' | 'bad' | 'neutral' }[]
  className?: string
  formatValue?: (n: number) => string
}) {
  const total = parts.reduce((n, p) => n + p.value, 0)
  if (total === 0) {
    return <div className={cn('py-6 text-center text-erp-xs text-muted-foreground', className)}>No outcomes yet.</div>
  }
  const shown = parts.filter((p) => p.value > 0)

  return (
    <div className={className}>
      <div className="flex h-5 w-full overflow-hidden rounded-md"
           role="img"
           aria-label={shown.map((p) => `${p.label}: ${p.value}`).join(', ')}>
        {shown.map((p, i) => (
          <div key={p.label} className="flex h-full" style={{ width: `${(p.value / total) * 100}%` }}>
            {i > 0 && <div className="w-0.5 shrink-0 bg-card" />}
            <div className={cn('h-full flex-1', OUTCOME_TONE[p.tone])} title={`${p.label}: ${formatValue(p.value)}`} />
          </div>
        ))}
      </div>
      <ul className="mt-2 grid gap-x-4 gap-y-1 text-erp-xs" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))' }}>
        {shown.map((p) => (
          <li key={p.label} className="flex items-center gap-1.5 min-w-0">
            <span className={cn('h-2 w-2 rounded-full shrink-0', OUTCOME_TONE[p.tone])} />
            <span className="truncate">{p.label}</span>
            <span className="ml-auto tabular-nums font-medium">{formatValue(p.value)}</span>
            <span className="text-muted-foreground tabular-nums w-9 text-right">
              {((p.value / total) * 100).toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}


// ── Phase 13 additions ──────────────────────────────────────────────────────

export interface QuadrantPoint {
  id: string
  label: string
  /** 0-100. Null means NOT SCORED and the point is drawn hollow at the origin gutter. */
  x: number | null
  y: number | null
  size?: number | null
  tone?: 'danger' | 'warn' | 'ok' | 'muted'
}

const QUADRANT_TONE: Record<string, string> = {
  danger: 'fill-rose-500/60 stroke-rose-600',
  warn: 'fill-amber-500/60 stroke-amber-600',
  ok: 'fill-emerald-500/60 stroke-emerald-600',
  muted: 'fill-slate-400/50 stroke-slate-500',
}

/**
 * The Threat Matrix: electability against vulnerability, with a bubble per target.
 *
 * ## It is keyboard-navigable because it is navigation
 *
 * This chart is the primary way into a target's file, not a decoration beside a table. A
 * chart that is the only route to a screen and responds to nothing but a mouse excludes
 * anybody using a keyboard or a screen reader from the feature entirely — so the points are
 * a `role="list"` of focusable circles, each with a `<title>`, and Enter selects.
 *
 * ## Unscored targets are shown, not dropped
 *
 * A target with no publishable polling estimate has `x = null`. It is rendered in a gutter
 * on the left with a hollow marker and counted in the caption, because a matrix that
 * silently omitted them would read as the complete field — and the candidates nobody has
 * measured are exactly the ones an analyst needs to notice.
 */
export function ScatterQuadrant({
  points, xLabel, yLabel, onSelect, height = 320,
}: {
  points: QuadrantPoint[]
  xLabel: string
  yLabel: string
  onSelect?: (id: string) => void
  height?: number
}) {
  const pad = 28
  const width = 520
  // The unscored lane is part of the chart, not a margin: it has to be wide enough to carry a
  // heading and the names, or "shown at the left edge" is true and unreadable.
  const gutter = 96
  const plotW = width - pad - gutter
  const plotH = height - pad * 2
  const scored = points.filter((p) => p.x != null && p.y != null)
  const unscored = points.filter((p) => p.x == null || p.y == null)

  const px = (v: number) => gutter + (Math.max(0, Math.min(100, v)) / 100) * plotW
  const py = (v: number) => pad + plotH - (Math.max(0, Math.min(100, v)) / 100) * plotH

  if (points.length === 0) {
    // An empty plot area is a big grey box that reads as "loading" or "broken". Say it.
    return (
      <p className="rounded border border-dashed border-border px-3 py-8 text-center
                    text-[12px] text-muted-foreground">
        Nothing to plot yet.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {/* Capped, not w-full: at full container width this viewBox scales to ~1050×650 and
          swallows the screen. The chart is one panel among several, not the page. */}
      <svg viewBox={`0 0 ${width} ${height}`} className="mx-auto block w-full max-w-2xl"
           role="group" aria-label={`${yLabel} against ${xLabel}`}>
        <rect x={gutter} y={pad} width={plotW} height={plotH}
              className="fill-muted/30 stroke-border" strokeWidth={1} />
        <line x1={px(50)} y1={pad} x2={px(50)} y2={pad + plotH}
              className="stroke-border" strokeDasharray="3 3" />
        <line x1={gutter} y1={py(50)} x2={gutter + plotW} y2={py(50)}
              className="stroke-border" strokeDasharray="3 3" />

        <text x={gutter + plotW / 2} y={height - 6} textAnchor="middle"
              className="fill-muted-foreground text-[10px]">{xLabel}</text>
        <text x={10} y={pad + plotH / 2} textAnchor="middle"
              transform={`rotate(-90 10 ${pad + plotH / 2})`}
              className="fill-muted-foreground text-[10px]">{yLabel}</text>

        {/* An EMPTY plane is a big grey box that reads as broken. When nothing can be placed
            on it, the box says so rather than leaving the reader to work it out. */}
        {scored.length === 0 && (
          <text x={gutter + plotW / 2} y={pad + plotH / 2} textAnchor="middle"
                className="fill-muted-foreground text-[11px]">
            Nothing plotted — no target has both scores yet
          </text>
        )}

        {unscored.length > 0 && (
          <text x={20} y={pad - 10} className="fill-muted-foreground text-[10px]">
            Not scored
          </text>
        )}

        <g role="list">
          {scored.map((p) => (
            <circle
              key={p.id}
              role="listitem"
              tabIndex={0}
              cx={px(p.x as number)}
              cy={py(p.y as number)}
              r={Math.max(4, Math.min(14, ((p.size ?? 40) / 100) * 14))}
              className={`${QUADRANT_TONE[p.tone ?? 'muted']} cursor-pointer outline-none
                          focus-visible:stroke-[3px]`}
              strokeWidth={1.5}
              onClick={() => onSelect?.(p.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSelect?.(p.id)
                }
              }}
            >
              <title>{`${p.label} — ${xLabel} ${Math.round(p.x as number)}, `
                + `${yLabel} ${Math.round(p.y as number)}`}</title>
            </circle>
          ))}
          {/* NAMED, not anonymous dots. An unscored target is one an analyst most needs to
              notice, and a hollow circle with no label is easier to ignore than an omission. */}
          {unscored.map((p, i) => (
            <g key={p.id} role="listitem" tabIndex={0}
               className="cursor-pointer outline-none [&:focus-visible_circle]:stroke-[3px]"
               onClick={() => onSelect?.(p.id)}
               onKeyDown={(e) => {
                 if (e.key === 'Enter' || e.key === ' ') {
                   e.preventDefault()
                   onSelect?.(p.id)
                 }
               }}>
              <title>{`${p.label} — not scored: no publishable polling estimate`}</title>
              <circle cx={14} cy={pad + 10 + i * 16} r={4}
                      className="fill-none stroke-muted-foreground" strokeWidth={1.5}
                      strokeDasharray="2 2" />
              <text x={24} y={pad + 13 + i * 16}
                    className="fill-muted-foreground text-[9px]">
                {p.label.length > 14 ? `${p.label.slice(0, 13)}…` : p.label}
              </text>
            </g>
          ))}
        </g>
      </svg>

      <div className="grid grid-cols-2 gap-1 text-[10px] text-muted-foreground sm:grid-cols-4">
        <span>Low {xLabel.toLowerCase()} · low {yLabel.toLowerCase()} — monitor</span>
        <span>High {xLabel.toLowerCase()} · low {yLabel.toLowerCase()} — hardest</span>
        <span>Low {xLabel.toLowerCase()} · high {yLabel.toLowerCase()} — contain</span>
        <span>High {xLabel.toLowerCase()} · high {yLabel.toLowerCase()} — priority</span>
      </div>

      {unscored.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {unscored.length} target{unscored.length === 1 ? '' : 's'} in the{' '}
          <b>Not scored</b> lane — a point needs BOTH axes, and a score is null when there is no publishable
          polling estimate behind it. Listed rather than omitted: an unmeasured candidate
          ranked at zero would sort below every measured one, which is precisely backwards.
        </p>
      )}
    </div>
  )
}

/**
 * Concentric arcs for a handful of related metrics — the Live Mood dial.
 *
 * A null metric leaves its ring empty and prints "—" rather than closing to zero. On a mood
 * panel that distinction is the whole point: "we measured this and it is low" and "nobody
 * measured this" look identical on a filled ring and mean opposite things.
 */
export function RadialMetrics({ metrics, size = 180 }: {
  metrics: { label: string; value: number | null; tone?: string }[]
  size?: number
}) {
  const centre = size / 2
  const ringGap = 14
  return (
    <div className="flex flex-wrap items-center gap-4">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
           aria-label={metrics.map((m) => `${m.label}: ${m.value ?? 'not measured'}`).join('; ')}>
        {metrics.map((m, i) => {
          const r = centre - 10 - i * ringGap
          if (r <= 6) return null
          const circumference = 2 * Math.PI * r
          const pct = m.value == null ? 0 : Math.max(0, Math.min(100, m.value)) / 100
          return (
            <g key={m.label}>
              <circle cx={centre} cy={centre} r={r} fill="none" strokeWidth={7}
                      className="stroke-muted" />
              {m.value != null && (
                <circle cx={centre} cy={centre} r={r} fill="none" strokeWidth={7}
                        strokeLinecap="round"
                        className={m.tone ?? 'stroke-primary'}
                        strokeDasharray={`${circumference * pct} ${circumference}`}
                        transform={`rotate(-90 ${centre} ${centre})`} />
              )}
            </g>
          )
        })}
      </svg>
      <ul className="space-y-1">
        {metrics.map((m) => (
          <li key={m.label} className="flex items-baseline gap-2 text-[11px]">
            <span className="text-muted-foreground">{m.label}</span>
            <span className="font-medium tabular-nums">
              {m.value == null ? 'not measured' : Math.round(m.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * A column board — the five-stage kill chain, and the operations board on the same code.
 *
 * Columns render even when empty, because an empty DEPLOY column is information: it says
 * nothing has cleared the evidence gate yet, which is different from the column not existing.
 */
export function StageBoard<T>({ columns, renderItem, onSelectColumn, showHints = false }: {
  columns: { key: string; label: string; hint?: string; items: T[] }[]
  renderItem: (item: T) => React.ReactNode
  onSelectColumn?: (key: string) => void
  /**
   * Print the hint under the column head instead of hiding it in the `title` tooltip.
   *
   * A hover tooltip is not discoverable — on a board whose whole point is that a card
   * cannot move until it has earned the move, the requirement has to be legible without
   * knowing to hover, or the gate reads as the software refusing for no reason.
   */
  showHints?: boolean
}) {
  return (
    <div className="axis-table-scroll grid grid-flow-col auto-cols-[minmax(180px,1fr)] gap-2">
      {columns.map((column) => (
        <div key={column.key} className="rounded border border-border bg-card">
          <button
            type="button"
            onClick={() => onSelectColumn?.(column.key)}
            title={column.hint}
            className="flex w-full items-baseline justify-between gap-2 border-b border-border
                       px-2 py-1.5 text-left"
          >
            <span className="text-[11px] font-semibold uppercase tracking-wide">
              {column.label}
            </span>
            <span className="rounded bg-muted px-1 text-[10px] tabular-nums">
              {column.items.length}
            </span>
          </button>
          {showHints && column.hint ? (
            <p className="border-b border-border px-2 py-1 text-[10px] leading-snug
                          text-muted-foreground">
              {column.hint}
            </p>
          ) : null}
          <div className="space-y-1 p-1.5">
            {column.items.length === 0 ? (
              <p className="px-1 py-2 text-[11px] text-muted-foreground">Nothing at this stage</p>
            ) : (
              column.items.map((item, i) => <div key={i}>{renderItem(item)}</div>)
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
